import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader } from '@/shared/components';
import {
  type UserScopeConfigurationResponseDto,
  useUserScopeConfiguration,
} from '@/entities/user';

type ScopePath = {
  [K in keyof UserScopeConfigurationResponseDto]: `${K}.${Extract<keyof UserScopeConfigurationResponseDto[K], string>}`;
}[keyof UserScopeConfigurationResponseDto];

type Props = {
  children: ReactNode;
  scopePath: ScopePath;
  redirectTo?: string;
};

export function ScopeGuard({ children, scopePath, redirectTo = '/' }: Props) {
  const { userScopeConfiguration, isUserScopeConfigurationLoading } =
    useUserScopeConfiguration();

  if (isUserScopeConfigurationLoading || !userScopeConfiguration) {
    return <Loader />;
  }

  const getNestedValue = (
    obj: UserScopeConfigurationResponseDto | undefined,
    path: string[]
  ): boolean => {
    if (!obj) return false;

    let current: unknown = obj;

    for (const key of path) {
      if (!current || typeof current !== 'object') {
        return false;
      }

      const record = current as Record<string, unknown>;
      if (!(key in record)) {
        return false;
      }

      current = record[key];
    }

    return typeof current === 'boolean' ? current : false;
  };

  const pathParts = scopePath.split('.');
  const hasAccess = getNestedValue(userScopeConfiguration, pathParts);

  if (!hasAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
